import { useMemo } from 'react'
import { createColumnHelper } from '@tanstack/react-table'
import { format } from 'date-fns'

import { DataTable, DataTableProvider } from 'components/DataTable'
import { DataTableContainer } from 'components/DataTable/UI'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import type { ScheduledEmail, ScheduledEmailsResponse } from '../sequences_api'

/**
 * ==================== SCHEDULED EMAILS TABLE ====================
 * NOTE: This displays data from a PASSTHROUGH API - data is fetched directly
 * from EmailBison API on each request, NOT from local database.
 * Always fresh, never stale.
 */

const STATUS_LABELS: Record<string, string> = {
  scheduled: 'Scheduled',
  sent: 'Sent',
  bounced: 'Bounced',
  replied: 'Replied',
  opened: 'Opened',
  clicked: 'Clicked',
  failed: 'Failed',
  cancelled: 'Cancelled',
}

const STATUS_COLORS: Record<string, string> = {
  scheduled: 'bg-blue-100 text-blue-800',
  sent: 'bg-green-100 text-green-800',
  bounced: 'bg-red-100 text-red-800',
  replied: 'bg-purple-100 text-purple-800',
  opened: 'bg-cyan-100 text-cyan-800',
  clicked: 'bg-indigo-100 text-indigo-800',
  failed: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800',
}

interface ScheduledEmailsTableProps {
  data: ScheduledEmailsResponse | undefined
  isLoading: boolean
}

const columnHelper = createColumnHelper<ScheduledEmail>()

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '-'
  try {
    return format(new Date(dateStr), 'MMM d, yyyy h:mm a')
  } catch {
    return dateStr
  }
}

function ScheduledEmailsTableContent({
  data,
  isLoading,
}: ScheduledEmailsTableProps) {
  const columns = useMemo(
    () => [
      columnHelper.accessor('lead_full_name', {
        header: 'Lead',
        size: 200,
        cell: (info) => {
          const name = info.getValue()
          const email = info.row.original.lead_email
          // Show dash if no lead data available
          if (!name && !email) {
            return <span className="text-sm text-fg-secondary">-</span>
          }
          return (
            <div>
              <p className="text-sm font-medium text-fg truncate">
                {name || '-'}
              </p>
              <p className="text-sm text-fg-secondary">{email || '-'}</p>
            </div>
          )
        },
      }),
      columnHelper.accessor('sender_email_name', {
        header: 'Sender',
        size: 180,
        cell: (info) => {
          const name = info.getValue()
          const email = info.row.original.sender_email_address
          // Show dash if no sender data available
          if (!name && !email) {
            return <span className="text-sm text-fg-secondary">-</span>
          }
          return (
            <div>
              <p className="text-sm font-medium text-fg truncate">
                {name || '-'}
              </p>
              <p className="text-sm text-fg-secondary">{email || '-'}</p>
            </div>
          )
        },
      }),
      columnHelper.accessor('email_subject', {
        header: 'Subject',
        size: 200,
        cell: (info) => (
          <p className="text-sm text-fg truncate" title={info.getValue() || ''}>
            {info.getValue() || '(no subject)'}
          </p>
        ),
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        size: 100,
        cell: (info) => {
          const status = info.getValue() || 'unknown'
          return (
            <span
              className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
                STATUS_COLORS[status] || 'bg-gray-100 text-gray-800'
              }`}
            >
              {STATUS_LABELS[status] || status}
            </span>
          )
        },
      }),
      columnHelper.accessor('scheduled_date_local', {
        header: 'Scheduled',
        size: 150,
        cell: (info) => (
          <span className="text-sm text-fg-secondary">
            {formatDateTime(info.getValue())}
          </span>
        ),
      }),
      columnHelper.accessor('sent_at', {
        header: 'Sent At',
        size: 150,
        cell: (info) => (
          <span className="text-sm text-fg-secondary">
            {formatDateTime(info.getValue())}
          </span>
        ),
      }),
      columnHelper.accessor('unique_opens', {
        header: 'Opens',
        size: 70,
        cell: (info) => (
          <span className="text-sm text-fg-secondary">{info.getValue()}</span>
        ),
      }),
      columnHelper.accessor('unique_replies', {
        header: 'Replies',
        size: 70,
        cell: (info) => (
          <span className="text-sm text-fg-secondary">{info.getValue()}</span>
        ),
      }),
    ],
    []
  )

  return (
    <DataTableContainer>
      <DataTable
        tableKey="scheduled-emails-table"
        data={data?.results ?? []}
        columns={columns}
        tableHeader={
          <div className="flex justify-between items-center w-full py-2">
            <p className="text-sm text-fg-secondary">
              {data?.count ?? 0} scheduled email
              {(data?.count ?? 0) !== 1 ? 's' : ''}
            </p>
          </div>
        }
        isPaginationEnabled={false}
        loading={isLoading}
      />
    </DataTableContainer>
  )
}

export function ScheduledEmailsTable(props: ScheduledEmailsTableProps) {
  return (
    <ColumnsStoreProvider tableKey="scheduled-emails-table">
      <DataTableProvider tableKey="scheduled-emails-table">
        <ScheduledEmailsTableContent {...props} />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}
